'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@lib/api/api-response';
import { readApiErrorMessage } from '@lib/api/utils/read-api-error-message';
import { type FlowDocumentProcessingState, type FlowDocumentProcessingStatus, flowDocumentProcessingChannel } from '@lib/flows/functions/channels/flow-document-processing-channel';
import { fetchFlowDocumentProcessingRealtimeToken } from '@lib/flows/utils/fetch-flow-document-processing-realtime-token.client';
import { resolveUserFacingCollectionMetaError, toUserFacingDocumentProcessingError } from '@lib/flows/utils/format-user-facing-document-processing-error';
import { replayMissedDocumentReadyFromFlow } from '@lib/flows/utils/replay-missed-document-ready-from-flow';
import { replayMissedFirstWaveFromFlow } from '@lib/flows/utils/replay-missed-first-wave-from-flow';
import { reportDocumentProcessingRealtimeTokenFailure } from '@lib/flows/utils/report-document-processing-realtime-token-failure.client';
import type { ProcessingStageId } from '@lib/upload-processing/types/processing-stage-id';
import type { UploadProcessingProgressUpdate } from '@lib/upload-processing/types/upload-processing-progress-update';
import { useRealtime } from 'inngest/react';
import { subscribe } from 'inngest/realtime';
import type { DocumentFlow } from '../types/document-flow';
import type { Flow } from '../types/flow';
import type { FlowCollectionMeta, FlowDocumentMeta } from '../types/flow-meta';
import { useFlowDocumentProcessingRealtimeToken } from './use-flow-document-processing-realtime-token';
const REALTIME_TOPICS = ['status'] as const;
/** Used when realtime cannot connect or the stream drops mid-wait. */
const POLL_INTERVAL_MS = 5_000;
/**
 * Fast durable-meta poll while waiting for Nutrient `documentId`. Live
 * `publish-document-ready` can be dropped on preview; replay from flow meta
 * must not wait on the slower terminal safety cadence.
 */
const DOCUMENT_READY_POLL_INTERVAL_MS = 750;
/**
 * While the realtime socket is open, still re-check durable flow meta on this
 * cadence. A background tab can keep the websocket "open" without delivering
 * the worker's `processed` publish (PORTAL-16D); without this, the client hangs
 * until DEFAULT_TIMEOUT_MS even though meta is already terminal.
 */
const SAFETY_POLL_INTERVAL_MS = 3_000;
const FLOW_FETCH_INIT: RequestInit = {
  cache: 'no-store'
};
// Must cover PDF parse (up to 60s) plus per-flow Inngest concurrency queuing
// a second document behind an in-flight job. Aligns with the worker-side
// waitForEvent timeout used by quick-sign / process completion.
const DEFAULT_TIMEOUT_MS = 15 * 60_000;
type FlowDocumentProcessingClientStatus = FlowDocumentProcessingStatus;
type FlowDocumentProcessingByCollectionId = Record<string, FlowDocumentProcessingClientStatus>;
export class FlowDocumentProcessingError extends ApiError {
  override readonly name = 'FlowDocumentProcessingError';
  readonly flowId: string;
  readonly collectionId: string;
  readonly state: Extract<FlowDocumentProcessingState, 'failed' | 'needs_reupload'>;
  constructor(args: {
    flowId: string;
    collectionId: string;
    state: Extract<FlowDocumentProcessingState, 'failed' | 'needs_reupload'>;
    message: string;
  }) {
    super(args.message, args.state === 'needs_reupload' ? 409 : 422);
    this.flowId = args.flowId;
    this.collectionId = args.collectionId;
    this.state = args.state;
  }
}
async function fetchFlow(flowId: string): Promise<Flow> {
  const response = await fetch(`/api/flows/${flowId}`, FLOW_FETCH_INIT);
  if (!response.ok) {
    throw new ApiError(await readApiErrorMessage(response), response.status);
  }
  return (await response.json()) as Flow;
}
function pendingNeedsDocumentReadyReplay(pending: ReadonlySet<string>, consumedDocumentReady: ReadonlySet<string>): boolean {
  for (const collectionId of pending) {
    if (!consumedDocumentReady.has(collectionId)) {
      return true;
    }
  }
  return false;
}
function getDocumentCollections(flow: Flow): FlowDocumentMeta['collections'] | undefined {
  const meta = flow.meta as FlowDocumentMeta | null | undefined;
  return meta?.collections;
}
function toClientStatus(flowId: string, collectionId: string, collectionMeta: FlowCollectionMeta): FlowDocumentProcessingClientStatus | null {
  if (!collectionMeta.status) {
    return null;
  }
  return {
    flowId,
    collectionId,
    state: collectionMeta.status === 'failed' && collectionMeta.needsReupload ? 'needs_reupload' : collectionMeta.status,
    ...(collectionMeta.error ? {
      message: resolveUserFacingCollectionMetaError(collectionMeta) ?? collectionMeta.error
    } : {}),
    ...(collectionMeta.detectionFailed ? {
      detectionFailed: true
    } : {})
  };
}
function seedStatusByCollectionId(flowId: string | undefined, flow: DocumentFlow | Flow | null | undefined): FlowDocumentProcessingByCollectionId {
  const seededFlowId = flowId ?? flow?.id;
  if (!seededFlowId || !flow) {
    return {};
  }
  const collections = getDocumentCollections(flow);
  if (!collections) {
    return {};
  }
  return Object.entries(collections).reduce<FlowDocumentProcessingByCollectionId>((acc, [collectionId, collectionMeta]) => {
    if (!collectionMeta) {
      return acc;
    }
    const seededStatus = toClientStatus(seededFlowId, collectionId, collectionMeta);
    if (!seededStatus) {
      return acc;
    }
    acc[collectionId] = seededStatus;
    return acc;
  }, {});
}
function toTerminalError(flowId: string, collectionId: string, collectionMeta: FlowCollectionMeta | undefined): FlowDocumentProcessingError | null {
  if (collectionMeta?.status !== 'failed') {
    return null;
  }
  return new FlowDocumentProcessingError({
    flowId,
    collectionId,
    state: collectionMeta.needsReupload ? 'needs_reupload' : 'failed',
    message: resolveUserFacingCollectionMetaError(collectionMeta) ?? 'Document processing failed. Please try again.'
  });
}
function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
type WaitOutcome = {
  kind: 'done';
} | {
  kind: 'pending';
} | {
  kind: 'error';
  error: FlowDocumentProcessingError;
};
function evaluateFlowCollections(flowId: string, collectionIds: string[], flow: Flow): WaitOutcome {
  const collections = getDocumentCollections(flow);
  let allProcessed = true;
  for (const collectionId of collectionIds) {
    const collectionMeta = collections?.[collectionId];
    const terminalError = toTerminalError(flowId, collectionId, collectionMeta);
    if (terminalError) {
      return {
        kind: 'error',
        error: terminalError
      };
    }
    if (collectionMeta?.status !== 'processed') {
      allProcessed = false;
    }
  }
  return allProcessed ? {
    kind: 'done'
  } : {
    kind: 'pending'
  };
}

/**
 * Like evaluateFlowCollections, but also drops collections that are already
 * `processed` from `pending`. Needed for catch-up GETs after subscribe: a
 * multi-file batch may have finished some collections before the websocket
 * opened, and without continuous polling those IDs would otherwise stick.
 */
function syncPendingFromFlow(flowId: string, pending: Set<string>, flow: Flow): WaitOutcome {
  const collections = getDocumentCollections(flow);
  for (const collectionId of [...pending]) {
    const collectionMeta = collections?.[collectionId];
    const terminalError = toTerminalError(flowId, collectionId, collectionMeta);
    if (terminalError) {
      return {
        kind: 'error',
        error: terminalError
      };
    }
    if (collectionMeta?.status === 'processed') {
      pending.delete(collectionId);
    }
  }
  return pending.size === 0 ? {
    kind: 'done'
  } : {
    kind: 'pending'
  };
}
function evaluateRealtimeStatus(flowId: string, collectionIds: ReadonlySet<string>, status: FlowDocumentProcessingStatus): WaitOutcome | {
  kind: 'progress';
  collectionId: string;
} {
  if (status.flowId !== flowId || !collectionIds.has(status.collectionId)) {
    return {
      kind: 'pending'
    };
  }
  if (status.state === 'processed') {
    return {
      kind: 'progress',
      collectionId: status.collectionId
    };
  }
  if (status.state === 'failed' || status.state === 'needs_reupload') {
    return {
      kind: 'error',
      error: new FlowDocumentProcessingError({
        flowId,
        collectionId: status.collectionId,
        state: status.state,
        message: toUserFacingDocumentProcessingError(status.message, status.state === 'needs_reupload' ? 'needs_reupload' : 'processing_failed')
      })
    };
  }
  return {
    kind: 'pending'
  };
}
async function pollUntilCollectionsProcessed({
  flowId,
  collectionIds,
  deadline,
  onStageChange,
  onProcessingProgress,
  onFirstWaveReady,
  consumedDocumentReady,
  consumedFirstWave
}: {
  flowId: string;
  collectionIds: string[];
  deadline: number;
  onStageChange?: (stage: ProcessingStageId) => void;
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void;
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void;
  consumedDocumentReady: Set<string>;
  consumedFirstWave: Set<string>;
}): Promise<void> {
  const pending = new Set(collectionIds);
  while (true) {
    const flow = await fetchFlow(flowId);
    replayMissedDocumentReadyFromFlow(flowId, pending, flow, consumedDocumentReady, onProcessingProgress, onStageChange);
    replayMissedFirstWaveFromFlow(flowId, pending, flow, consumedFirstWave, onFirstWaveReady, onProcessingProgress);
    const outcome = evaluateFlowCollections(flowId, collectionIds, flow);
    if (outcome.kind === 'error') {
      throw outcome.error;
    }
    if (outcome.kind === 'done') {
      return;
    }
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) {
      throw new ApiError('Timed out waiting for document processing to finish.', 408);
    }
    const pollIntervalMs = pendingNeedsDocumentReadyReplay(pending, consumedDocumentReady) ? DOCUMENT_READY_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    await sleep(Math.min(pollIntervalMs, remainingMs));
  }
}

/**
 * Waits until every collection reaches a terminal meta state.
 *
 * Primary signal: Inngest Realtime `status` topic.
 * Durable flow GETs are used for:
 *   - the initial seed (may already be terminal)
 *   - one catch-up read right after the websocket opens (publishes that raced
 *     subscribe)
 *   - a low-frequency safety poll while the socket stays open (covers silent
 *     websocket stalls, e.g. background tabs missing a published terminal)
 *   - a poll fallback if the token cannot be minted, subscribe fails, or the
 *     stream drops before a terminal arrives
 */
export async function waitForCollectionsProcessed({
  flowId,
  collectionIds,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  onStageChange,
  onProcessingProgress,
  onFirstWaveReady
}: {
  flowId: string;
  collectionIds: string[];
  timeoutMs?: number;
  onStageChange?: (stage: ProcessingStageId) => void;
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void;
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void;
}): Promise<void> {
  if (collectionIds.length === 0) return;
  const deadline = Date.now() + timeoutMs;
  const flow = await fetchFlow(flowId);
  const initial = evaluateFlowCollections(flowId, collectionIds, flow);
  if (initial.kind === 'error') {
    throw initial.error;
  }
  if (initial.kind === 'done') {
    return;
  }
  const collections = getDocumentCollections(flow);
  const pending = new Set(collectionIds.filter(collectionId => collections?.[collectionId]?.status !== 'processed'));
  if (pending.size === 0) {
    return;
  }
  const consumedDocumentReady = new Set<string>();
  const consumedFirstWave = new Set<string>();
  replayMissedDocumentReadyFromFlow(flowId, pending, flow, consumedDocumentReady, onProcessingProgress, onStageChange);
  let token: {
    key: string;
    apiBaseUrl?: string;
  };
  try {
    token = await fetchFlowDocumentProcessingRealtimeToken(flowId);
  } catch (error) {
    reportDocumentProcessingRealtimeTokenFailure({
      flowId,
      error,
      surface: 'wait_for_processed'
    });
    await pollUntilCollectionsProcessed({
      flowId,
      collectionIds: [...pending],
      deadline,
      onStageChange,
      onProcessingProgress,
      onFirstWaveReady,
      consumedDocumentReady,
      consumedFirstWave
    });
    return;
  }
  const channel = flowDocumentProcessingChannel({
    flowId
  });
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let subscription: {
      close: (reason?: string) => void;
    } | undefined;
    const replayProgressFromFlow = (flowSnapshot: Flow) => {
      replayMissedDocumentReadyFromFlow(flowId, pending, flowSnapshot, consumedDocumentReady, onProcessingProgress, onStageChange);
      replayMissedFirstWaveFromFlow(flowId, pending, flowSnapshot, consumedFirstWave, onFirstWaveReady, onProcessingProgress);
    };
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutTimer);
      subscription?.close('waitForCollectionsProcessed finished');
      if (error) {
        reject(error);
        return;
      }
      resolve();
    };
    const timeoutTimer = setTimeout(() => {
      finish(new ApiError('Timed out waiting for document processing to finish.', 408));
    }, Math.max(0, deadline - Date.now()));

    // Realtime / safety-poll / stream-drop may all observe "pending empty" at
    // once. Only one confirm loop should run; finish() must not win the race
    // and skip replica-lag retries.
    let confirmingDurable = false;
    const confirmDurableAndFinish = async () => {
      if (settled || confirmingDurable) return;
      confirmingDurable = true;
      // Realtime publishes after the worker CAS merge, but a following getFlow
      // can still race a replica. Confirm durable meta before resolving.
      for (let attempt = 0; attempt < 5; attempt++) {
        if (settled) return;
        try {
          const outcome = evaluateFlowCollections(flowId, collectionIds, await fetchFlow(flowId));
          if (outcome.kind === 'error') {
            finish(outcome.error);
            return;
          }
          if (outcome.kind === 'done') {
            finish();
            return;
          }
        } catch {
          // Retry briefly; fall through to finish if realtime already said done.
        }
        await sleep(100 * (attempt + 1));
      }
      if (!settled) {
        finish();
      }
    };
    const buildProgressUpdate = (status: FlowDocumentProcessingStatus): UploadProcessingProgressUpdate => ({
      flowId: status.flowId,
      collectionId: status.collectionId,
      stage: status.stage,
      pagesProcessed: status.pagesProcessed,
      totalPages: status.totalPages,
      fieldsDetected: status.fieldsDetected,
      wave: status.wave,
      firstWaveReady: status.firstWaveReady,
      progressLabel: status.progressLabel,
      documentId: status.documentId,
      documentUrl: status.documentUrl
    });
    const handleStatusMessage = (status: FlowDocumentProcessingStatus) => {
      if (status.flowId !== flowId || !pending.has(status.collectionId)) {
        return;
      }
      if (status.state === 'processing') {
        if (status.documentId) {
          consumedDocumentReady.add(status.collectionId);
        }
        if (status.stage) {
          onStageChange?.(status.stage);
        }
        onProcessingProgress?.(buildProgressUpdate(status));
      }
      if (status.firstWaveReady === true && !consumedFirstWave.has(status.collectionId)) {
        consumedFirstWave.add(status.collectionId);
        onFirstWaveReady?.(buildProgressUpdate(status));
      }
      const outcome = evaluateRealtimeStatus(flowId, pending, status);
      if (outcome.kind === 'error') {
        finish(outcome.error);
        return;
      }
      if (outcome.kind === 'progress') {
        pending.delete(outcome.collectionId);
        if (pending.size === 0) {
          void confirmDurableAndFinish();
        }
      }
    };

    // Read the stream returned by `subscribe` itself. Calling getJsonStream()
    // again creates a second fan-out view that misses any messages published
    // between connect and the second stream creation (common for fast PDFs).
    void subscribe({
      channel,
      topics: ['status'] as ['status'],
      key: token.key,
      apiBaseUrl: token.apiBaseUrl
    }).then(async active => {
      if (settled) {
        active.close('waitForCollectionsProcessed already settled');
        return;
      }
      subscription = active;

      // Catch publishes that landed before the websocket was open. Prune any
      // collections that already reached `processed` in durable meta.
      // Transient GET failures are ignored — realtime (or later poll) continues.
      try {
        const flow = await fetchFlow(flowId);
        const outcome = syncPendingFromFlow(flowId, pending, flow);
        if (outcome.kind === 'error') {
          finish(outcome.error);
          return;
        }
        if (outcome.kind === 'done') {
          finish();
          return;
        }
        replayProgressFromFlow(flow);
      } catch {
        // leave pending as-is; stream may still deliver terminals
      }

      // Safety net: durable meta can reach a terminal while the realtime
      // stream stays open without delivering (common when the tab is
      // backgrounded). Race this against the stream reader below.
      void (async () => {
        while (!settled) {
          const remainingMs = deadline - Date.now();
          if (remainingMs <= 0) {
            return;
          }
          const safetyPollIntervalMs = pendingNeedsDocumentReadyReplay(pending, consumedDocumentReady) ? DOCUMENT_READY_POLL_INTERVAL_MS : SAFETY_POLL_INTERVAL_MS;
          await sleep(Math.min(safetyPollIntervalMs, remainingMs));
          if (settled) return;
          // Realtime may already have cleared `pending` and started durable
          // confirm. An empty set is not proof of durable terminal — never
          // call finish() here or we skip replica-lag retries.
          if (pending.size === 0) {
            void confirmDurableAndFinish();
            return;
          }
          try {
            const flow = await fetchFlow(flowId);
            const outcome = syncPendingFromFlow(flowId, pending, flow);
            if (outcome.kind === 'error') {
              finish(outcome.error);
              return;
            }
            if (outcome.kind === 'done') {
              void confirmDurableAndFinish();
              return;
            }
            replayProgressFromFlow(flow);
          } catch {
            // Transient GET failure — realtime / next safety poll continues.
          }
        }
      })();
      const reader = active.getReader();
      try {
        while (!settled) {
          const {
            done,
            value: message
          } = await reader.read();
          if (done || !message) {
            break;
          }
          // Ignore lifecycle / ping frames; topic publishes use kind "data".
          if (message.topic !== 'status' || !message.data) {
            continue;
          }
          handleStatusMessage(message.data as FlowDocumentProcessingStatus);
        }
      } catch {
        // Stream fault — fall through to durable poll fallback.
      } finally {
        try {
          reader.releaseLock();
        } catch {
          // already released
        }
      }
      if (settled) return;

      // Stream closed. If realtime already cleared pending, finish via the
      // durable confirm path — syncPendingFromFlow would treat an empty set
      // as done without inspecting the flow payload.
      if (pending.size === 0) {
        void confirmDurableAndFinish();
        return;
      }

      // Stream closed without a terminal: one more durable check, then poll.
      // A blip on this GET must not skip the poll fallback below.
      try {
        const outcome = syncPendingFromFlow(flowId, pending, await fetchFlow(flowId));
        if (outcome.kind === 'error') {
          finish(outcome.error);
          return;
        }
        if (outcome.kind === 'done') {
          void confirmDurableAndFinish();
          return;
        }
      } catch {
        // continue to poll
      }
      try {
        await pollUntilCollectionsProcessed({
          flowId,
          collectionIds: [...pending],
          deadline,
          onStageChange,
          onProcessingProgress,
          onFirstWaveReady,
          consumedDocumentReady,
          consumedFirstWave
        });
        finish();
      } catch (error) {
        finish(error);
      }
    }).catch(() => {
      // Subscribe/connect failed; durable poll is the only remaining signal.
      void pollUntilCollectionsProcessed({
        flowId,
        collectionIds: [...pending],
        deadline,
        onStageChange,
        onProcessingProgress,
        onFirstWaveReady,
        consumedDocumentReady,
        consumedFirstWave
      }).then(() => finish(), error => finish(error));
    });
  });
}
export function useFlowDocumentProcessing(flowId, options) {
  const $ = _c(29);
  options?.seedFlow;
  const t0 = options?.seedFlow;
  let t1;
  if ($[0] !== flowId || $[1] !== t0) {
    t1 = seedStatusByCollectionId(flowId, t0);
    $[0] = flowId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const seededStatuses = t1;
  const [statusByCollectionId, setStatusByCollectionId] = useState(seededStatuses);
  const previousFlowIdRef = useRef(flowId);
  let t2;
  let t3;
  if ($[3] !== flowId || $[4] !== seededStatuses) {
    t2 = () => {
      if (previousFlowIdRef.current !== flowId) {
        previousFlowIdRef.current = flowId;
        setStatusByCollectionId(seededStatuses);
        return;
      }
      setStatusByCollectionId(current => {
        const merged = {
          ...seededStatuses
        };
        for (const [collectionId, status] of Object.entries(current)) {
          const seed = seededStatuses[collectionId];
          const seedIsTerminal = !!seed && (seed.state === "processed" || seed.state === "failed" || seed.state === "needs_reupload");
          const currentIsTerminal = status.state === "processed" || status.state === "failed" || status.state === "needs_reupload";
          if (!seed) {
            merged[collectionId] = status;
            continue;
          }
          if (seed.state === "processed") {
            continue;
          }
          if (status.state === "processed") {
            merged[collectionId] = status;
            continue;
          }
          if (status.state === "processing" && (seed.state === "failed" || seed.state === "needs_reupload")) {
            merged[collectionId] = status;
            continue;
          }
          if (seedIsTerminal) {
            continue;
          }
          if (currentIsTerminal) {
            merged[collectionId] = status;
          }
        }
        return merged;
      });
    };
    t3 = [flowId, seededStatuses];
    $[3] = flowId;
    $[4] = seededStatuses;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const t4 = Boolean(flowId);
  let t5;
  if ($[7] !== flowId || $[8] !== t4) {
    t5 = {
      flowId,
      mintEnabled: t4
    };
    $[7] = flowId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    clientToken,
    realtimeSubscribeEnabled
  } = useFlowDocumentProcessingRealtimeToken(t5);
  let t6;
  if ($[10] !== flowId) {
    t6 = flowId ? flowDocumentProcessingChannel({
      flowId
    }) : undefined;
    $[10] = flowId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const channel = t6;
  const t7 = clientToken ?? undefined;
  let t8;
  if ($[12] !== channel || $[13] !== realtimeSubscribeEnabled || $[14] !== t7) {
    t8 = {
      channel,
      topics: REALTIME_TOPICS,
      token: t7,
      enabled: realtimeSubscribeEnabled,
      historyLimit: 50,
      pauseOnHidden: false
    };
    $[12] = channel;
    $[13] = realtimeSubscribeEnabled;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const realtime = useRealtime(t8);
  const allRealtimeMessages = realtime.messages.all;
  const realtimeStatus = realtime.messages.byTopic.status?.data ?? null;
  const processedMessageCountRef = useRef(0);
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      processedMessageCountRef.current = 0;
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== flowId) {
    t10 = [flowId];
    $[17] = flowId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] !== allRealtimeMessages || $[20] !== flowId) {
    t11 = () => {
      if (!flowId) {
        return;
      }
      if (allRealtimeMessages.length <= processedMessageCountRef.current) {
        return;
      }
      const freshMessages = allRealtimeMessages.slice(processedMessageCountRef.current);
      processedMessageCountRef.current = allRealtimeMessages.length;
      const updates = freshMessages.flatMap(message => {
        if (message.topic !== "status") {
          return [];
        }
        const status_0 = message.data as FlowDocumentProcessingStatus | undefined;
        if (!status_0 || status_0.flowId !== flowId) {
          return [];
        }
        return [status_0];
      });
      if (updates.length === 0) {
        return;
      }
      setStatusByCollectionId(current_0 => {
        const next = {
          ...current_0
        };
        for (const status_1 of updates) {
          next[status_1.collectionId] = status_1;
        }
        return next;
      });
    };
    t12 = [flowId, allRealtimeMessages];
    $[19] = allRealtimeMessages;
    $[20] = flowId;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  bb0: {
    if (!flowId || !realtimeStatus || realtimeStatus.flowId !== flowId) {
      t13 = null;
      break bb0;
    }
    t13 = realtimeStatus;
  }
  const latestStatus = t13;
  let t14;
  if ($[23] !== latestStatus || $[24] !== realtime.connectionStatus || $[25] !== realtime.error || $[26] !== realtime.runStatus || $[27] !== statusByCollectionId) {
    t14 = {
      connectionStatus: realtime.connectionStatus,
      runStatus: realtime.runStatus,
      error: realtime.error,
      latestStatus,
      statusByCollectionId
    };
    $[23] = latestStatus;
    $[24] = realtime.connectionStatus;
    $[25] = realtime.error;
    $[26] = realtime.runStatus;
    $[27] = statusByCollectionId;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}